import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import type { Deal } from '@/types/clientGroups';


import { Icon, type IconName } from '@/components/common/Icon';
import { useLocalForecast, WMO } from '@/components/home/HomeownerHub';
import { MARKET_SEATS } from '@/constants/profileOptions';
import { useDashboardLayout } from '@/context/DashboardLayoutProvider';
import { usePropertiesContext } from '@/context/PropertiesProvider';
import { useAdminProperties } from '@/hooks/admin/useAdminProperties';
import { useClientGroups } from '@/hooks/useClientGroups';
import { useUserProfile } from '@/hooks/user/useUserProfile';
import { useRecentPropertyIds } from '@/hooks/useRecentPropertyIds';
import { useUnreadMessages } from '@/hooks/useUnreadMessages';
import { useUserRole } from '@/hooks/useUserRole';
import { adminPropertyToUserProperty } from '@/services/api/admin';
import { calculateRevenueForDeal } from '@/utils/dealFees';

/*
 * Today, for advisors: the Tradehaus Today screen (components/tx/Today.jsx + styles/tx-ds.css, the .tday/.tw recipes)
 * carried over in its own design language and pointed at an advisor's day: the deadlines coming due, unread clients,
 * the pipeline, the weather in their market, and the properties being worked. It belongs to no one property: the
 * property picker, client card and tools panel stay off it (DashboardLayout). Widgets come in Tradehaus's three sizes on its six-column grid; each opens its page.
 */

// spacing follows Hauser's frame: 20px between cards (10px on a phone), the same gutter as every other page
const CSS = `
.hx-today { padding: 20px 0 40px; }
.hx-today .tday-in { width: min(1080px, 100%); margin: 0 auto; }
.hx-today .tday-h { position: relative; padding: 4px 4px 18px; }
.hx-today .tday-kicker { font-size: 12px; font-weight: 600; color: var(--tx-muted); }
.hx-today .tday-h h1 { margin: 10px 0 0; font-size: 34px; font-weight: 300; letter-spacing: -0.035em; line-height: 1.1; color: var(--tx-text); }
.hx-today .tday-clock { position: absolute; right: 4px; bottom: 22px; display: flex; align-items: center; gap: 10px; color: var(--tx-text); }
.hx-today .tday-clock svg { width: 40px; height: 40px; flex: none; }
.hx-today .tday-clock .face { fill: rgba(var(--tx-ink-rgb), 0.08); stroke: rgba(var(--tx-ink-rgb), 0.28); stroke-width: 0.75; }
.hx-today .tday-clock .tick { stroke: rgba(var(--tx-ink-rgb), 0.45); stroke-width: 0.9; stroke-linecap: round; }
.hx-today .tday-clock .hand { stroke: var(--tx-text); stroke-width: 1.4; stroke-linecap: round; }
.hx-today .tday-clock .min { stroke-width: 1; }
.hx-today .tday-clock .pin { fill: var(--tx-text); }
.hx-today .tday-clock b { display: block; font-size: 15px; font-weight: 500; letter-spacing: -0.02em; font-variant-numeric: tabular-nums; }
.hx-today .tday-clock small { display: block; font-size: 11px; font-weight: 500; color: var(--tx-muted); }

.hx-today .tday-grid { display: grid; grid-template-columns: repeat(6, minmax(0, 1fr)); grid-auto-rows: 176px; gap: 20px; grid-auto-flow: row dense; }
.hx-today .tw { position: relative; display: flex; flex-direction: column; min-width: 0; padding: 20px; border-radius: 24px; color: var(--tx-text);
  background: var(--tx-glass-panel); background-image: var(--tx-surface-light); box-shadow: var(--tx-shadow);
  animation: hx-tw-in 0.55s cubic-bezier(.2, .9, .3, 1) both; animation-delay: calc(var(--i, 0) * 55ms); transition: transform 0.18s cubic-bezier(.2, .9, .3, 1); }
.hx-today .tw-md { grid-column: span 2; }
.hx-today .tw-lg { grid-column: span 2; grid-row: span 2; }
.hx-today .tw:has(> .tw-open:active) { transform: scale(0.98); }
@keyframes hx-tw-in { from { opacity: 0; transform: translateY(10px) scale(0.97); } to { opacity: 1; transform: none; } }
@media (prefers-reduced-motion: reduce) { .hx-today .tw { animation: none; transition: none; } }
.hx-today .tw-open { position: absolute; inset: 0; z-index: 0; border: 0; padding: 0; margin: 0; border-radius: inherit; background: transparent; cursor: pointer; }
.hx-today .tw-open:focus-visible { outline: 2px solid rgba(128, 128, 128, 0.8); outline-offset: 2px; }
.hx-today .tw > :not(.tw-open) { position: relative; z-index: 1; pointer-events: none; }
.hx-today .tw button:not(.tw-open) { pointer-events: auto; }
.hx-today .tw-h { display: flex; align-items: center; gap: 6px; min-height: 16px; font-size: 12px; font-weight: 600; color: var(--tx-muted); }
.hx-today .tw-h svg { color: var(--tx-text); }
.hx-today .tw-h em { margin-left: auto; font-style: normal; font-weight: 500; color: var(--tx-faint); font-variant-numeric: tabular-nums;
  min-width: 0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.hx-today .tw-meta { font-size: 11px; font-weight: 500; color: var(--tx-muted); }
.hx-today .tw-line { display: block; font-size: 13px; font-weight: 600; letter-spacing: -0.01em; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.hx-today .tw-foot { margin-top: auto; display: flex; flex-direction: column; gap: 2px; min-width: 0; }
.hx-today .tw-empty { margin-top: auto; display: flex; flex-direction: column; gap: 4px; }
.hx-today .tw-empty b { font-size: 17px; font-weight: 500; letter-spacing: -0.02em; }
.hx-today .tw-empty span { font-size: 11px; font-weight: 500; color: var(--tx-muted); line-height: 1.4; }
.hx-today .tw-skel { margin-top: auto; height: 40px; border-radius: 10px; background: var(--tx-fill); }

.hx-today .tw-fill { flex: 1; display: flex; flex-direction: column; justify-content: space-between; min-height: 0; }
.hx-today .tw-fig { display: flex; align-items: baseline; gap: 8px; margin-top: 6px; }
.hx-today .tw-fig b { font-size: 44px; font-weight: 300; letter-spacing: -0.04em; line-height: 1; font-variant-numeric: tabular-nums; }
.hx-today .tw-fig span { font-size: 11px; font-weight: 500; color: var(--tx-muted); }
.hx-today .tw-agenda { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 6px; }
.hx-today .tw-agenda li { display: flex; align-items: center; gap: 8px; min-width: 0; font-size: 11px; font-weight: 600; }
.hx-today .tw-agenda li i { flex: none; width: 6px; height: 6px; border-radius: 999px; background: rgba(var(--tx-ink-rgb), 0.3); }
.hx-today .tw-agenda li.soon i { background: #FF9A4D; }
.hx-today .tw-agenda li.late i { background: #DB5282; }
.hx-today .tw-agenda li span { flex: 1; min-width: 0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.hx-today .tw-agenda li em { flex: none; font-style: normal; font-weight: 500; color: var(--tx-faint); font-variant-numeric: tabular-nums; }

.hx-today .tw-take { flex: 1; display: flex; flex-direction: column; justify-content: space-between; min-height: 0; padding-top: 10px; }
.hx-today .tw-take p { margin: 0; font-size: 17px; font-weight: 400; letter-spacing: -0.015em; line-height: 1.35; text-wrap: balance;
  display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden; }
.hx-today .tw-take .tw-meta { display: inline-flex; align-items: center; gap: 4px; }

.hx-today .tw-pipe { flex: 1; display: flex; flex-direction: column; justify-content: flex-end; gap: 10px; min-height: 0; }
.hx-today .tw-bars { display: grid; grid-template-columns: repeat(5, minmax(0, 1fr)); gap: 10px; align-items: end; }
.hx-today .tw-bar { display: flex; flex-direction: column; gap: 5px; min-width: 0; }
.hx-today .tw-bar b { font-size: 17px; font-weight: 500; letter-spacing: -0.02em; font-variant-numeric: tabular-nums; }
.hx-today .tw-bar i { display: block; width: 10px; height: 40px; border-radius: 999px; background: rgba(var(--tx-ink-rgb), 0.08); position: relative;
  overflow: hidden; }
.hx-today .tw-bar i::after { content: ''; position: absolute; left: 0; right: 0; bottom: 0; height: calc(var(--h, 0) * 100%); border-radius: inherit;
  background: var(--tx-text); opacity: 0.55; animation: hx-tw-grow 0.8s cubic-bezier(.2, .9, .3, 1) both 0.25s; transform-origin: bottom; }
.hx-today .tw-bar.won i::after { background: #c0ff02; opacity: 1; }
@keyframes hx-tw-grow { from { transform: scaleY(0); } to { transform: none; } }
@media (prefers-reduced-motion: reduce) { .hx-today .tw-bar i::after { animation: none; } }
.hx-today .tw-bar span { font-size: 10px; font-weight: 600; color: var(--tx-muted); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

.hx-today .tw-sky { flex: 1; display: flex; flex-direction: column; min-height: 0; }
.hx-today .tw-sky .now { display: flex; align-items: center; gap: 10px; margin-top: 6px; }
.hx-today .tw-sky .now b { font-size: 44px; font-weight: 300; letter-spacing: -0.04em; line-height: 1; font-variant-numeric: tabular-nums; }
/* the days ahead as columns (Tradehaus .tw-sky .later): three at the bottom right of a wide card, on the line with the
   condition and rain, so the figure stands alone at the top; six across a large one */
.hx-today .tw-sky { position: relative; }
.hx-today .tw-sky .later { list-style: none; margin: 0; padding: 0; position: absolute; right: 0; bottom: 0; display: flex; gap: 18px; }
.hx-today .tw-sky .later.row { position: static; margin: auto 0 18px; justify-content: space-between; gap: 8px; }
.hx-today .tw-sky .later li { display: flex; flex-direction: column; align-items: center; gap: 6px; }
.hx-today .tw-sky .later span { font-size: 10px; font-weight: 600; color: var(--tx-muted); white-space: nowrap; }
.hx-today .tw-sky .later b { font-size: 13px; font-weight: 500; font-variant-numeric: tabular-nums; color: var(--tx-text); }
/* the high on the condition's row, the low on the rain's (2px apart, as those two are) */
.hx-today .tw-sky .later small { margin-top: -4px; font-size: 11px; font-weight: 500; color: var(--tx-faint); font-variant-numeric: tabular-nums; }
.hx-today.wall .tw-sky .later span { color: rgba(255, 255, 255, 0.74); }
.hx-today.wall .tw-sky .later small { color: rgba(255, 255, 255, 0.62); }

.hx-today .tw-picks { list-style: none; margin: auto 0 0; padding: 0; display: flex; flex-direction: column; gap: 6px; }
.hx-today .tw-picks button { display: flex; align-items: center; gap: 12px; padding: 4px 6px 4px 4px; margin: 0 -4px; width: calc(100% + 8px); border: 0;
  border-radius: 10px; background: transparent; color: inherit; text-align: left; cursor: pointer; font-family: inherit; }
.hx-today .tw-picks button:hover { background: var(--tx-fill); }
.hx-today .tw-picks button > span:not(.tw-thumb) { flex: 1; min-width: 0; }
.hx-today .tw-picks b { display: block; font-size: 13px; font-weight: 600; letter-spacing: -0.01em; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.hx-today .tw-picks small { display: block; margin-top: 1px; font-size: 11px; font-weight: 500; color: var(--tx-muted);
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.hx-today .tw-picks .chev { color: var(--tx-faint); flex: none; transform: rotate(-90deg); }
.hx-today .tw-thumb { flex: none; width: 44px; height: 44px; border-radius: 10px; display: grid; place-items: center;
  background: var(--tx-fill); color: var(--tx-faint); }

.hx-today .tw-note { flex: 1; display: flex; flex-direction: column; min-height: 0; margin-top: 10px; }
.hx-today .tw-note textarea { flex: 1; width: 100%; resize: none; border: 0; outline: none; background: transparent; color: var(--tx-text);
  font: inherit; font-size: 13px; font-weight: 500; line-height: 1.5; pointer-events: auto; }
.hx-today .tw-note textarea::placeholder { color: var(--tx-faint); }
.hx-today .tw-wallpaper { flex: 1; margin: 10px -6px -6px; border-radius: 16px; background-size: cover; background-position: center;
  display: flex; align-items: flex-end; padding: 10px 12px;
  background-color: var(--tx-fill); }
.hx-today .tw-wallpaper b { font-size: 12px; font-weight: 600; color: #fff; text-shadow: 0 1px 8px rgba(0, 0, 0, 0.45); }

/* arranging (long-press any widget): the jiggle, the − to remove, the size button, a dashed slot where it lands */
.hx-today .tday-grid { position: relative; }
.hx-today .tday-grid .tw { touch-action: manipulation; -webkit-user-select: none; user-select: none; -webkit-touch-callout: none;
  transition: transform 0.18s cubic-bezier(.2, .9, .3, 1), translate 0.32s cubic-bezier(.2, .9, .3, 1), scale 0.2s ease, box-shadow 0.2s ease; }
.hx-today .tday-grid.settled .tw { animation: none; }
.hx-today .tday-grid.editing .tw { touch-action: none; cursor: grab; overflow: visible; animation: hx-jiggle 0.26s ease-in-out infinite alternate; }
.hx-today .tday-grid.editing .tw:nth-child(2n) { animation-delay: -0.13s; animation-direction: alternate-reverse; }
.hx-today .tday-grid.editing .tw.tw-md, .hx-today .tday-grid.editing .tw.tw-lg { animation-name: hx-jiggle-md; }
.hx-today .tday-grid.editing .tw > * { pointer-events: none !important; }
.hx-today .tday-grid.editing .tw .tw-size, .hx-today .tday-grid.editing .tw .tw-rm { pointer-events: auto !important; }
.hx-today .tday-grid.editing .tw-h em { visibility: hidden; }
.hx-today .tday-grid .tw.lift { z-index: 5; scale: 1.04; cursor: grabbing; animation: none; box-shadow: 0 24px 48px -18px rgba(18, 18, 17, 0.45); }
@keyframes hx-jiggle { from { rotate: -1deg; } to { rotate: 1deg; } }
@keyframes hx-jiggle-md { from { rotate: -0.5deg; } to { rotate: 0.5deg; } }
@media (prefers-reduced-motion: reduce) { .hx-today .tday-grid.editing .tw { animation: none;
  box-shadow: var(--tx-shadow), 0 0 0 1.5px rgba(var(--tx-ink-rgb), 0.25); } }
.hx-today .tw-slot { position: absolute; display: none; pointer-events: none; z-index: 0; box-sizing: border-box; border-radius: 24px;
  border: 1.5px dashed rgba(var(--tx-ink-rgb), 0.28); background: rgba(var(--tx-ink-rgb), 0.03);
  transition: left 0.32s cubic-bezier(.2, .9, .3, 1), top 0.32s cubic-bezier(.2, .9, .3, 1),
    width 0.32s cubic-bezier(.2, .9, .3, 1), height 0.32s cubic-bezier(.2, .9, .3, 1); }
.hx-today .tw-rm { position: absolute !important; z-index: 4; top: -8px; left: -8px; width: 26px; height: 26px; display: grid; place-items: center; border: 0;
  border-radius: 999px; cursor: pointer; background: rgba(var(--tx-ink-rgb), 0.16); color: var(--tx-text); backdrop-filter: blur(20px) saturate(1.6);
  box-shadow: inset 0 0 0 0.5px rgba(var(--tx-ink-rgb), 0.25); }
.hx-today .tw-size { position: absolute !important; z-index: 3; top: 12px; right: 12px; width: 28px; height: 28px; display: grid; place-items: center; border: 0;
  border-radius: 8px; cursor: pointer; background: var(--tx-glass-card); color: var(--tx-text); box-shadow: var(--tx-shadow-sm); }
.hx-today .tday-done { position: absolute; right: 4px; bottom: 22px; height: 32px; padding: 0 16px; border: 0; border-radius: 10px; cursor: pointer;
  background: var(--tx-lime); color: var(--tx-on-lime); font: inherit; font-size: 13px; font-weight: 600; letter-spacing: -0.02em; }

/* below the widgets, past a hairline: the widgets not on Today and the wallpapers */
.hx-today .tday-more { display: flex; flex-direction: column; gap: 22px; margin-top: 28px; padding-top: 24px; border-top: 1px solid rgba(var(--tx-ink-rgb), 0.22);
  animation: hx-more-in 0.4s cubic-bezier(.2, .9, .3, 1) both; }
@keyframes hx-more-in { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: none; } }
.hx-today .tday-more .tday-kicker { margin: 0 0 10px; color: rgba(var(--tx-ink-rgb), 0.86); }
.hx-today .tday-shelf { display: grid; grid-template-columns: repeat(auto-fill, minmax(220px, 1fr)); gap: 10px; }
.hx-today .tday-shelf-item { display: flex; align-items: center; gap: 12px; padding: 12px; border-radius: 16px; background: var(--tx-glass-panel);
  box-shadow: var(--tx-shadow-sm); }
.hx-today .tday-shelf-item .ico { width: 36px; height: 36px; display: grid; place-items: center; border-radius: 10px; background: var(--tx-fill);
  color: var(--tx-text); flex: none; }
.hx-today .tday-shelf-item .txt { flex: 1; min-width: 0; }
.hx-today .tday-shelf-item b { display: block; font-size: 13px; font-weight: 600; }
.hx-today .tday-shelf-item small { display: block; font-size: 11px; font-weight: 500; color: var(--tx-muted); white-space: nowrap; overflow: hidden;
  text-overflow: ellipsis; }
.hx-today .tw-add { width: 28px; height: 28px; display: grid; place-items: center; border: 0; border-radius: 8px; cursor: pointer;
  background: var(--tx-fill); color: var(--tx-text); flex: none; }
.hx-today .tw-add:hover { background: var(--tx-fill-2); }
.hx-today .tday-shelf-none { margin: 0; font-size: 12px; font-weight: 500; color: var(--tx-muted); }
.hx-today .tday-walls { display: grid; grid-template-columns: repeat(auto-fill, minmax(150px, 1fr)); gap: 10px; }
.hx-today .tday-wall-tile { position: relative; aspect-ratio: 3 / 2; border-radius: 14px; }
.hx-today .tday-wall-tile .pick { position: absolute; inset: 0; padding: 0; border: 0; border-radius: inherit; overflow: hidden; cursor: pointer; color: #fff;
  background: var(--tx-ground); background-size: cover; background-position: center; }
.hx-today .tday-wall-tile .pick::after { content: ''; position: absolute; inset: 0; border-radius: inherit;
  background: linear-gradient(to bottom, transparent 55%, rgba(0, 0, 0, 0.5)); }
.hx-today .tday-wall-tile span { position: absolute; z-index: 1; left: 10px; right: 10px; bottom: 8px; font-size: 11px; font-weight: 600; text-align: left;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.hx-today .tday-wall-tile.on .pick { box-shadow: 0 0 0 2px #c0ff02; }
.hx-today .tday-wall-tile .x { position: absolute; z-index: 2; top: 6px; right: 6px; width: 22px; height: 22px; display: grid; place-items: center; border: 0;
  border-radius: 999px; cursor: pointer; background: rgba(0, 0, 0, 0.45); color: #fff; }
.hx-today .tday-wall-add { aspect-ratio: 3 / 2; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 6px;
  border: 1.5px dashed rgba(var(--tx-ink-rgb), 0.28);
  border-radius: 14px; background: transparent; color: var(--tx-muted); cursor: pointer; font: inherit; font-size: 11px; font-weight: 600; }
.hx-today .tday-wall-add:hover { color: var(--tx-text); border-color: rgba(var(--tx-ink-rgb), 0.45); }
.hx-today .tday-err { font-size: 11px; font-weight: 500; color: #FF9A4D; margin-top: 8px; }

/* the wallpaper: full frame behind everything, the widgets turning to glass over it (Tradehaus .tday.wall) */
.hx-today .hx-wall { position: fixed; inset: 0; z-index: 0; background-size: cover; background-position: center; transition: opacity 0.8s ease; }
.hx-today .tday-in { position: relative; z-index: 1; }
/* a fade over every photo: darker behind the greeting at the top, a light veil below, so white type reads on any sky */
.hx-today .hx-wall::after { content: ''; position: absolute; inset: 0;
  background: linear-gradient(180deg, rgba(0, 0, 0, 0.42) 0%, rgba(0, 0, 0, 0.12) 30%, rgba(0, 0, 0, 0.1) 100%); }
.hx-today.wall .tday-kicker { color: rgba(255, 255, 255, 0.82); text-shadow: 0 1px 12px rgba(0, 0, 0, 0.25); }
.hx-today.wall .tday-h h1 { line-height: 1.2; padding-bottom: 0.08em; color: transparent; -webkit-background-clip: text; background-clip: text;
  background-image: linear-gradient(180deg, rgba(255, 255, 255, 0.98) 0%, rgba(255, 255, 255, 0.86) 55%, rgba(255, 255, 255, 0.7) 100%);
  filter: drop-shadow(0 2px 18px rgba(0, 0, 0, 0.22)); }
.hx-today.wall .tday-clock { color: #fff; }
.hx-today.wall .tw { background: rgba(28, 28, 27, 0.16); background-image: none; backdrop-filter: blur(42px) saturate(1.7) brightness(1.04);
  -webkit-backdrop-filter: blur(42px) saturate(1.7) brightness(1.04);
  box-shadow: inset 0 0.5px 0 rgba(255, 255, 255, 0.5), inset 0 0 0 0.5px rgba(255, 255, 255, 0.22), 0 18px 40px -22px rgba(0, 0, 0, 0.5); }
.hx-today.wall .tw-h { color: rgba(255, 255, 255, 0.82); }
/* over a photo the quiet text needs more lift than on slate, or a bright sky swallows it */
.hx-today.wall .tw-meta, .hx-today.wall .tw-fig span, .hx-today.wall .tw-bar span, .hx-today.wall .tw-picks small,
.hx-today.wall .tw-empty span { color: rgba(255, 255, 255, 0.74); }
.hx-today.wall .tw-h em, .hx-today.wall .tw-agenda li em, .hx-today.wall .tday-clock small { color: rgba(255, 255, 255, 0.62); }
.hx-today.wall .tw-bar i { background: rgba(255, 255, 255, 0.14); }
.hx-today.wall .tw-bar i::after { background: #fff; opacity: 0.8; }
.hx-today.wall .tw-bar.won i::after { background: #c0ff02; opacity: 1; }
.hx-today.wall .tw-thumb { background: rgba(255, 255, 255, 0.12); }
.hx-today.wall .tw-rm { background: rgba(255, 255, 255, 0.28); color: #fff; }
.hx-today.wall .tw-size { background: rgba(255, 255, 255, 0.2); color: #fff; backdrop-filter: blur(16px); box-shadow: none; }
.hx-today.wall .tw-slot { border-color: rgba(255, 255, 255, 0.55); background: rgba(255, 255, 255, 0.06); }
.hx-today.wall .tday-more { border-top-color: rgba(255, 255, 255, 0.3); }
.hx-today.wall .tday-more .tday-kicker { color: rgba(255, 255, 255, 0.86); }

@media (max-width: 800px) {
  .hx-today { padding: 0 0 24px; }
  .hx-today .tday-h { padding: 6px 4px 16px; }
  .hx-today .tday-h h1 { font-size: 30px; }
  .hx-today .tday-clock { display: none; }
  .hx-today .tday-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); grid-auto-rows: auto; gap: 10px; }
  .hx-today .tw-sm { aspect-ratio: 1; }
  .hx-today .tw-md, .hx-today .tw-lg { grid-column: span 2; min-height: 176px; }
}
`;

// the deal's engagement window, the same arithmetic as the sidebar's context tile (PropertyAdvisors)
const PERIOD_DAYS: Record<string, number> = { Initial: 90, 'Ext I': 90, 'Ext II': 90, 'Ext III': 90 };
const daysLeft = (deal: Deal): number | null => {
  if (!deal.engagementStart) return null;
  const start = new Date(`${deal.engagementStart}T00:00:00`);
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const elapsed = Math.floor((now.getTime() - start.getTime()) / 86_400_000);
  const total = PERIOD_DAYS[deal.periodType] || 90;
  return Math.max(0, Math.min(total - Math.max(0, elapsed), total));
};

const STAGES: { label: string; statuses: string[]; won?: boolean }[] = [
  { label: 'Search', statuses: ['Search'] },
  { label: 'Diligence', statuses: ['Due Diligence'] },
  { label: 'Offer', statuses: ['Offer'] },
  { label: 'Contract', statuses: ['Under Contract', 'Pending'] },
  { label: 'Closed', statuses: ['Closed'], won: true },
];

const WMO_GLYPH: Record<string, IconName> = { sun: 'sun', cloud: 'cloud', shower: 'rain', rain: 'rain', snow: 'snow', storm: 'storm' };

const greeting = (d: Date) => {
  const h = d.getHours();
  return h < 12 ? 'Good morning' : h < 17 ? 'Good afternoon' : 'Good evening';
};

// the local time as an analog dial and a digital reading (Tradehaus's Clock)
const Clock = ({ at }: { at: Date }) => {
  const h = at.getHours();
  const m = at.getMinutes();
  const ha = ((h % 12) + m / 60) * 30;
  const ma = m * 6;
  return (
    <div className="tday-clock" aria-label={`Local time ${at.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`}>
      <svg viewBox="0 0 40 40" aria-hidden="true">
        <circle cx="20" cy="20" r="18.5" className="face" />
        {Array.from({ length: 12 }, (_, j) => (
          <line key={j} x1="20" y1="3.8" x2="20" y2={j % 3 ? 5.2 : 6.4} transform={`rotate(${j * 30} 20 20)`} className="tick" />
        ))}
        <line x1="20" y1="20" x2="20" y2="11" transform={`rotate(${ha} 20 20)`} className="hand" />
        <line x1="20" y1="20" x2="20" y2="7.5" transform={`rotate(${ma} 20 20)`} className="hand min" />
        <circle cx="20" cy="20" r="1.3" className="pin" />
      </svg>
      <div>
        <b>{at.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }).replace(/\s?[AP]M/, '')}</b>
        <small>{h < 12 ? 'am' : 'pm'} · Local</small>
      </div>
    </div>
  );
};

type Size = 'sm' | 'md' | 'lg';
type WidgetId = 'deadlines' | 'messages' | 'pipeline' | 'weather' | 'clients' | 'properties' | 'fees' | 'newin' | 'notes' | 'wallpaper';
type Layout = { id: WidgetId; size: Size }[];

// what each widget is (for the shelf) and the sizes it comes in: 1×, 2× wide, and the 2×2 square; its first the default
const ABOUT: Record<WidgetId, { icon: IconName; name: string; about: string }> = {
  deadlines: { icon: 'calendar', name: 'Deadlines', about: 'Engagements running down' },
  messages: { icon: 'mail', name: 'Messages', about: 'Clients waiting on a reply' },
  pipeline: { icon: 'funnel', name: 'Pipeline', about: 'Your deals by stage' },
  weather: { icon: 'sun', name: 'Weather', about: 'The forecast in your market' },
  clients: { icon: 'team', name: 'Clients', about: 'Active clients and open deals' },
  properties: { icon: 'home', name: 'Properties', about: 'The ones you looked at last' },
  fees: { icon: 'dollar', name: 'Fees', about: 'Closed this year and in contract' },
  newin: { icon: 'bell', name: 'New in', about: 'Properties updated this week' },
  notes: { icon: 'note', name: 'Notes', about: 'A note to yourself' },
  wallpaper: { icon: 'landscape', name: 'Wallpaper', about: 'The picture behind Today' },
};
const SIZES: Record<WidgetId, Size[]> = {
  deadlines: ['sm', 'md', 'lg'],
  messages: ['sm', 'md', 'lg'],
  pipeline: ['md', 'lg', 'sm'],
  weather: ['sm', 'md', 'lg'],
  clients: ['sm', 'md', 'lg'],
  properties: ['md', 'lg', 'sm'],
  fees: ['sm', 'md'],
  newin: ['md', 'lg', 'sm'],
  notes: ['sm', 'md', 'lg'],
  wallpaper: ['md', 'sm'],
};
const SIZE_NAME: Record<Size, string> = { sm: 'Small', md: 'Wide', lg: 'Large' };
const DEFAULT_LAYOUT: Layout = [
  { id: 'deadlines', size: 'sm' },
  { id: 'messages', size: 'sm' },
  { id: 'newin', size: 'md' },
  { id: 'pipeline', size: 'md' },
  { id: 'weather', size: 'sm' },
  { id: 'clients', size: 'sm' },
  { id: 'properties', size: 'md' },
  { id: 'wallpaper', size: 'md' },
];
const HOLD_MS = 480;
const SLOP = 8;

// the arrangement and the wallpaper are a per-browser convenience (storage can be unavailable, so every access is guarded)
const LAYOUT_KEY = 'hauser.today.layout.v1';
const WALL_KEY = 'hauser.today.wall.v1';
const HERE_KEY = 'hauser.today.here.v1';   // the advisor's location for the weather, once they've shared it
const OWN_WALLS_KEY = 'hauser.today.ownWalls.v1';
const readJSON = <T,>(key: string, fallback: T): T => {
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
};
const writeJSON = (key: string, value: unknown): string | null => {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
    return null;
  } catch {
    return 'Couldn’t save that on this browser';
  }
};
const readLayout = (): Layout => {
  const saved = readJSON<Layout | null>(LAYOUT_KEY, null);
  if (!Array.isArray(saved)) return DEFAULT_LAYOUT;
  return saved.filter((w) => w && w.id in ABOUT && SIZES[w.id].includes(w.size));
};

// wallpapers: the plain Hauser ground, the photos shipped with the app, and the advisor's own (kept small, in this browser)
type Wall = { id: string; name: string; css: string; thumb?: string; own?: boolean };
const BUILT_IN_WALLS: Wall[] = [
  { id: 'none', name: 'None', css: 'none' },
  // photos shipped with the app (public/today/walls)
  { id: 'reynisdrangar', name: 'Reynisdrangar', css: 'url("/today/walls/reynisdrangar.jpg")', thumb: 'url("/today/walls/reynisdrangar-thumb.jpg")' },
  { id: 'griffith', name: 'Griffith Park', css: 'url("/today/walls/griffith.jpg")', thumb: 'url("/today/walls/griffith-thumb.jpg")' },
];
const OWN_MAX = 3;

// a photo cut down for the wallpaper: 2000px on its long side, a JPEG small enough to keep in this browser
const cutPhoto = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, 2000 / Math.max(img.width, img.height));
      const c = document.createElement('canvas');
      c.width = Math.round(img.width * scale);
      c.height = Math.round(img.height * scale);
      const ctx = c.getContext('2d');
      if (!ctx) return reject(new Error('Couldn’t read that photo'));
      ctx.drawImage(img, 0, 0, c.width, c.height);
      URL.revokeObjectURL(url);
      resolve(c.toDataURL('image/jpeg', 0.78));
    };
    img.onerror = () => reject(new Error('Couldn’t read that photo'));
    img.src = url;
  });

const Widget = ({
  id,
  size,
  i,
  aside,
  onOpen,
  editing,
  arrange,
  onSize,
  onRemove,
  children,
}: {
  id: WidgetId;
  size: Size;
  i: number;
  aside?: string | null;
  onOpen?: () => void;
  editing: boolean;
  arrange: React.HTMLAttributes<HTMLElement>;
  onSize: (id: WidgetId, size: Size) => void;
  onRemove: (id: WidgetId) => void;
  children: React.ReactNode;
}) => {
  const { icon, name } = ABOUT[id];
  const sizes = SIZES[id];
  const next = sizes[(sizes.indexOf(size) + 1) % sizes.length];
  return (
    <section className={`tw tw-${size}`} style={{ '--i': i } as React.CSSProperties} data-w={id} {...arrange}>
      {/* the whole widget opens its page; controls inside it sit above this */}
      {onOpen && <button type="button" className="tw-open" aria-label={`Open ${name}`} onClick={onOpen} tabIndex={-1} />}
      <header className="tw-h">
        <Icon name={icon} size={14} />
        <span>{name}</span>
        {aside && <em>{aside}</em>}
      </header>
      {children}
      {editing && (
        <button type="button" className="tw-rm" aria-label={`Remove ${name}`} onClick={() => onRemove(id)}>
          <Icon name="minus" size={14} stroke={2.4} />
        </button>
      )}
      {editing && sizes.length > 1 && (
        <button
          type="button"
          className="tw-size"
          aria-label={`${name} is ${SIZE_NAME[size]}. Make it ${SIZE_NAME[next]}`}
          onClick={() => onSize(id, next)}>
          <Icon name="resize" size={15} />
        </button>
      )}
    </section>
  );
};

export const TodayView = () => {
  const navigate = useNavigate();
  const { openInbox, isLeftSidebarCollapsed, setLeftSidebarCollapsed } = useDashboardLayout();
  // the daily greeting: the sidebar folds to its rail on arrival, and goes back to how it was on the way out
  const sidebarWas = useRef(isLeftSidebarCollapsed);
  useEffect(() => {
    const was = sidebarWas.current;
    setLeftSidebarCollapsed(true);
    return () => setLeftSidebarCollapsed(was);
  }, []);
  const { properties, setSelectedProperty } = usePropertiesContext();
  const { groups, isLoading: groupsLoading } = useClientGroups();
  const { totalUnread } = useUnreadMessages();
  const { userProfile } = useUserProfile();
  // advisors see every property they work (the switcher's list), not only the ones they own
  const { canViewAllProperties } = useUserRole();
  const { properties: adminProperties } = useAdminProperties({ enabled: canViewAllProperties });
  const { recentIds } = useRecentPropertyIds();

  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 30_000);
    return () => clearInterval(t);
  }, []);

  // ── arranging: long-press a widget to jiggle; drag to reorder, − to remove, the size button to resize ──
  const [layout, setLayout] = useState<Layout>(readLayout);
  const [editing, setEditing] = useState(false);
  const [picking, setPicking] = useState(false); // the wallpaper picker, opened from the Wallpaper widget
  const [settled, setSettled] = useState(false);
  const [note, setNote] = useState<string>(() => readJSON<string>('hauser.today.note.v1', ''));
  useEffect(() => {
    const t = setTimeout(() => writeJSON('hauser.today.note.v1', note), 400);
    return () => clearTimeout(t);
  }, [note]);
  const ending = () => {
    setEditing(false);
    setPicking(false);
  };
  const order = layout.map((w) => w.id);
  const grid = useRef<HTMLDivElement>(null);
  const slot = useRef<HTMLElement>(null);
  const hold = useRef<{ t: ReturnType<typeof setTimeout>; x: number; y: number } | null>(null);
  const drag = useRef<{ id: WidgetId; x: number; y: number; gx: number; gy: number; pid: number; el: HTMLElement } | null>(null);
  const before = useRef<Record<string, [number, number]> | null>(null);
  const place = (k: string) => grid.current?.querySelector<HTMLElement>(`[data-w="${k}"]`) ?? null;
  const snapshot = () =>
    Object.fromEntries(order.map((k) => {
      const el = place(k);
      return [k, el ? [el.offsetLeft, el.offsetTop] : [0, 0]];
    })) as Record<string, [number, number]>;
  const moveTo = (k: WidgetId, to: number) =>
    setLayout((l) => {
      const from = l.findIndex((w) => w.id === k);
      if (from < 0 || to === from || to < 0 || to >= l.length) return l;
      before.current = snapshot();
      const n = l.slice();
      const [w] = n.splice(from, 1);
      n.splice(to, 0, w);
      return n;
    });
  const remove = (k: WidgetId) => {
    before.current = snapshot();
    setLayout((l) => l.filter((w) => w.id !== k));
  };
  const add = (k: WidgetId) => {
    before.current = snapshot();
    setLayout((l) => (l.some((w) => w.id === k) ? l : [...l, { id: k, size: SIZES[k][0] }]));
  };
  const resize = (k: WidgetId, size: Size) => {
    before.current = snapshot();
    setLayout((l) => l.map((w) => (w.id === k ? { ...w, size } : w)));
  };
  const follow = (d: NonNullable<typeof drag.current>) => {
    const el = place(d.id);
    const g = grid.current?.getBoundingClientRect();
    if (!el || !g) return;
    el.style.translate = `${d.x - g.left - d.gx - el.offsetLeft}px ${d.y - g.top - d.gy - el.offsetTop}px`;
    const o = slot.current;
    if (o) {
      Object.assign(o.style, {
        display: 'block',
        left: `${el.offsetLeft}px`,
        top: `${el.offsetTop}px`,
        width: `${el.offsetWidth}px`,
        height: `${el.offsetHeight}px`,
      });
    }
  };
  // the widgets that moved glide from their old place to the new one; the one being dragged stays under the pointer
  useLayoutEffect(() => {
    const prev = before.current;
    before.current = null;
    for (const k of order) {
      const el = place(k);
      if (!el) continue;
      if (drag.current && drag.current.id === k) {
        follow(drag.current);
        continue;
      }
      if (!prev || !prev[k]) continue;
      const dx = prev[k][0] - el.offsetLeft;
      const dy = prev[k][1] - el.offsetTop;
      if (!dx && !dy) continue;
      el.style.transition = 'none';
      el.style.translate = `${dx}px ${dy}px`;
      el.getBoundingClientRect(); // commit the old place before gliding from it
      el.style.transition = '';
      el.style.translate = '';
    }
  }, [layout]);
  useEffect(() => {
    writeJSON(LAYOUT_KEY, layout);
  }, [layout]);
  useEffect(() => {
    if (!editing) return undefined;
    const key = (e: KeyboardEvent) => {
      if (e.key === 'Escape' || (e.key === 'Enter' && !(e.target as HTMLElement).closest('textarea'))) ending();
    };
    window.addEventListener('keydown', key);
    return () => window.removeEventListener('keydown', key);
  }, [editing]);
  const endDrag = (e: React.PointerEvent) => {
    const d = drag.current;
    if (!d || d.pid !== e.pointerId) return;
    drag.current = null;
    d.el.classList.remove('lift');
    d.el.style.translate = '';
    if (slot.current) slot.current.style.display = 'none';
  };
  const arrange = (id: WidgetId): React.HTMLAttributes<HTMLElement> => ({
    tabIndex: editing ? 0 : undefined,
    'aria-roledescription': editing ? 'Movable widget' : undefined,
    onKeyDown: editing
      ? (e) => {
          const at = order.indexOf(id);
          const d = ({ ArrowLeft: -1, ArrowUp: -1, ArrowRight: 1, ArrowDown: 1 } as Record<string, number>)[e.key];
          if (d) {
            e.preventDefault();
            moveTo(id, at + d);
          }
        }
      : undefined,
    onContextMenu: (e) => e.preventDefault(),
    onPointerDown: (e) => {
      if (e.button !== 0 || (e.target as HTMLElement).closest('.tw-size, .tw-rm, .tw-picks button, textarea')) return;
      const r = e.currentTarget.getBoundingClientRect();
      const start = { id, x: e.clientX, y: e.clientY, gx: e.clientX - r.left, gy: e.clientY - r.top, pid: e.pointerId, el: e.currentTarget as HTMLElement };
      if (editing) {
        e.preventDefault();
        e.currentTarget.setPointerCapture(e.pointerId);
        drag.current = start;
        e.currentTarget.classList.add('lift');
        return;
      }
      if (hold.current) clearTimeout(hold.current.t);
      hold.current = {
        t: setTimeout(() => {
          hold.current = null;
          setEditing(true);
          setSettled(true);
          try {
            navigator.vibrate?.(8);
          } catch {
            /* no haptics */
          }
        }, HOLD_MS),
        x: e.clientX,
        y: e.clientY,
      };
    },
    onPointerMove: (e) => {
      if (hold.current && Math.hypot(e.clientX - hold.current.x, e.clientY - hold.current.y) > SLOP) {
        clearTimeout(hold.current.t);
        hold.current = null;
      }
      const d = drag.current;
      if (!d || d.pid !== e.pointerId || !grid.current) return;
      d.x = e.clientX;
      d.y = e.clientY;
      follow(d);
      const g = grid.current.getBoundingClientRect();
      const px = e.clientX - g.left;
      const py = e.clientY - g.top;
      const over = order.find((k) => {
        if (k === d.id) return false;
        const el = place(k);
        return el && px > el.offsetLeft && px < el.offsetLeft + el.offsetWidth && py > el.offsetTop && py < el.offsetTop + el.offsetHeight;
      });
      if (over) moveTo(d.id, order.indexOf(over));
    },
    onPointerUp: (e) => {
      if (hold.current) {
        clearTimeout(hold.current.t);
        hold.current = null;
      }
      endDrag(e);
    },
    onPointerCancel: (e) => {
      if (hold.current) {
        clearTimeout(hold.current.t);
        hold.current = null;
      }
      endDrag(e);
    },
  });

  // ── the wallpaper ──
  const [ownWalls, setOwnWalls] = useState<Wall[]>(() => readJSON<Wall[]>(OWN_WALLS_KEY, []));
  const [wallId, setWallId] = useState<string>(() => readJSON<string>(WALL_KEY, 'reynisdrangar'));
  const [wallErr, setWallErr] = useState<string | null>(null);
  const [wallBusy, setWallBusy] = useState(false);
  const fileIn = useRef<HTMLInputElement>(null);
  const walls = [...BUILT_IN_WALLS, ...ownWalls];
  const wall = walls.find((w) => w.id === wallId) ?? BUILT_IN_WALLS[0];
  const pickWall = (id: string) => {
    setWallId(id);
    writeJSON(WALL_KEY, id);
  };
  const upload = async (file?: File) => {
    if (!file) return;
    setWallErr(null);
    setWallBusy(true);
    try {
      const data = await cutPhoto(file);
      const w: Wall = { id: `own-${Date.now()}`, name: file.name.replace(/\.[^.]+$/, ''), css: `url("${data}")`, own: true };
      const nextOwn = [...ownWalls, w];
      const err = writeJSON(OWN_WALLS_KEY, nextOwn);
      if (err) throw new Error('That photo is too big to keep on this browser');
      setOwnWalls(nextOwn);
      pickWall(w.id);
    } catch (e) {
      setWallErr(e instanceof Error ? e.message : 'Couldn’t add that photo');
    }
    setWallBusy(false);
  };
  const deleteOwn = (id: string) => {
    const nextOwn = ownWalls.filter((w) => w.id !== id);
    setOwnWalls(nextOwn);
    writeJSON(OWN_WALLS_KEY, nextOwn);
    if (wallId === id) pickWall('none');
  };
  const hasWall = wall.id !== 'none';

  // ── data ──
  const activeGroups = useMemo(() => groups.filter((g) => g.status === 'active'), [groups]);
  const deals = useMemo(
    () => activeGroups.flatMap((g) => g.deals.map((d) => ({ deal: d, client: g.holdingName }))),
    [activeGroups],
  );
  const openDeals = deals.filter(({ deal }) => deal.status !== 'Closed');
  const dueSoon = openDeals
    .map((x) => ({ ...x, days: daysLeft(x.deal) }))
    .filter((x): x is typeof x & { days: number } => x.days != null)
    .sort((a, b) => a.days - b.days);
  const thisFortnight = dueSoon.filter((x) => x.days <= 14).length;
  const stageCounts = STAGES.map((st) => ({ ...st, n: deals.filter(({ deal }) => st.statuses.includes(deal.status)).length }));
  const stageMax = Math.max(1, ...stageCounts.map((st) => st.n));
  // Today isn't about one property: the weather is where the advisor is (the browser's location, asked only when they tap
  // the card), else their own market (their first in Settings) read at its county seat
  const [here, setHere] = useState<{ lat: number; lon: number } | null>(() => readJSON(HERE_KEY, null));
  const locate = () =>
    navigator.geolocation?.getCurrentPosition(
      ({ coords }) => {
        const at = { lat: coords.latitude, lon: coords.longitude };
        setHere(at);
        writeJSON(HERE_KEY, at);
      },
      () => undefined,
      { maximumAge: 3600000, timeout: 10000 },
    );
  const market = userProfile?.markets?.[0];
  const seat = market ? MARKET_SEATS[market] : undefined;
  const { forecast } = useLocalForecast(seat ?? '', '', '', 'California', here);
  const today = forecast?.[0];
  const sky = today ? WMO[today.code] ?? { label: 'Clear', icon: 'sun' as const } : null;
  const workable = useMemo(
    () => (canViewAllProperties ? (adminProperties ?? []).map(adminPropertyToUserProperty) : properties ?? []),
    [canViewAllProperties, adminProperties, properties],
  );
  const recentAll = useMemo(() => {
    const byId = new Map(workable.map((p) => [p.property.id, p]));
    const viewed = recentIds.map((rid) => byId.get(rid)).filter((p): p is (typeof workable)[number] => Boolean(p));
    const rest = [...workable].sort((a, b) => b.property.updatedAt - a.property.updatedAt);
    return [...viewed, ...rest.filter((p) => !viewed.includes(p))];
  }, [workable, recentIds]);
  // Fees: what closed this year, and what's in contract now (the Clients & Deals fee arithmetic)
  const yearStart = new Date(now.getFullYear(), 0, 1).getTime() / 1000;
  const closedFees = deals
    .filter(({ deal }) => deal.status === 'Closed' && (deal.closedDate ?? 0) >= yearStart)
    .reduce((sum, { deal }) => sum + (calculateRevenueForDeal(deal) ?? 0), 0);
  const contractFees = deals
    .filter(({ deal }) => deal.status === 'Under Contract' || deal.status === 'Pending')
    .reduce((sum, { deal }) => sum + (calculateRevenueForDeal(deal) ?? 0), 0);
  const money = (n: number) => (n >= 1000 ? `$${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}K` : `$${Math.round(n)}`);
  // New in: properties touched in the last week
  const weekAgo = now.getTime() - 7 * 86_400_000;
  const freshest = [...workable].sort((a, b) => b.property.updatedAt - a.property.updatedAt);
  const freshThisWeek = freshest.filter((p) => p.property.updatedAt * (p.property.updatedAt < 1e12 ? 1000 : 1) >= weekAgo);
  const ago = (t: number) => {
    const ms = t < 1e12 ? t * 1000 : t;
    const d = Math.floor((now.getTime() - ms) / 86_400_000);
    return d <= 0 ? 'today' : d === 1 ? 'yesterday' : `${d} days ago`;
  };
  const firstName = userProfile?.firstName?.trim();
  const sizeOf = (k: WidgetId) => layout.find((w) => w.id === k)?.size ?? SIZES[k][0];

  const common = (k: WidgetId) => ({
    id: k,
    size: sizeOf(k),
    i: order.indexOf(k),
    editing,
    arrange: arrange(k),
    onSize: resize,
    onRemove: remove,
  });

  // each widget shows more as it grows (the agenda, the picks), never a stretched copy of the small one
  const cards: Record<WidgetId, React.ReactNode> = {
    deadlines: (
      <Widget key="deadlines" {...common('deadlines')} onOpen={() => navigate('/dashboard/clients')}>
        {groupsLoading ? (
          <div className="tw-skel" />
        ) : !dueSoon.length ? (
          <div className="tw-empty">
            <b>Nothing due</b>
            <span>No open engagements are running down</span>
          </div>
        ) : (
          <div className="tw-fill">
            <div className="tw-fig">
              <b>{thisFortnight}</b>
              <span>within 14 days</span>
            </div>
            <ul className="tw-agenda">
              {dueSoon.slice(0, sizeOf('deadlines') === 'lg' ? 7 : sizeOf('deadlines') === 'md' ? 3 : 2).map(({ deal, client, days }) => (
                <li key={deal.id} className={days <= 7 ? 'late' : days <= 14 ? 'soon' : undefined}>
                  <i />
                  <span>{sizeOf('deadlines') === 'sm' ? client : `${client} · ${deal.name}`}</span>
                  <em>{days === 0 ? 'Ended' : `${days}d`}</em>
                </li>
              ))}
            </ul>
          </div>
        )}
      </Widget>
    ),
    messages: (
      <Widget key="messages" {...common('messages')} onOpen={openInbox}>
        <div className="tw-fill">
          <div className="tw-fig">
            <b>{totalUnread}</b>
            <span>unread</span>
          </div>
          <div className="tw-foot">
            <span className="tw-line">{totalUnread ? 'Clients are waiting on you' : 'Inbox clear'}</span>
            <span className="tw-meta">{activeGroups.length} active clients</span>
          </div>
        </div>
      </Widget>
    ),
    pipeline: (
      <Widget key="pipeline" {...common('pipeline')} aside={`${openDeals.length} open`} onOpen={() => navigate('/dashboard/clients')}>
        {groupsLoading ? (
          <div className="tw-skel" />
        ) : sizeOf('pipeline') === 'sm' ? (
          <div className="tw-fill">
            <div className="tw-fig">
              <b>{openDeals.length}</b>
              <span>open deals</span>
            </div>
            <span className="tw-meta">{stageCounts[4].n} closed</span>
          </div>
        ) : (
          <div className="tw-pipe">
            <div className="tw-bars">
              {stageCounts.map((st) => (
                <div key={st.label} className={`tw-bar${st.won ? ' won' : ''}`}>
                  <b>{st.n}</b>
                  <i style={{ '--h': st.n / stageMax, height: sizeOf('pipeline') === 'lg' ? 180 : 40 } as React.CSSProperties} />
                  <span>{st.label}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </Widget>
    ),
    weather: (
      <Widget
        key="weather"
        {...common('weather')}
        aside={here ? 'Your location' : market ?? null}
        onOpen={here ? undefined : locate}>
        {!here && !seat ? (
          <div className="tw-empty">
            <b>Where’s your day?</b>
            <span>Tap to use your location</span>
          </div>
        ) : !today || !sky ? (
          <div className="tw-skel" />
        ) : (
          <div className="tw-sky">
            <div className="now">
              <Icon name={WMO_GLYPH[sky.icon] ?? 'sun'} size={34} />
              <b>{today.hi}°</b>
            </div>
            {sizeOf('weather') !== 'sm' && forecast && (
              <ol className={`later${sizeOf('weather') === 'lg' ? ' row' : ''}`}>
                {forecast.slice(1, sizeOf('weather') === 'lg' ? 7 : 4).map((d) => (
                  <li key={d.day}>
                    <span>{d.day}</span>
                    <Icon name={WMO_GLYPH[(WMO[d.code] ?? { icon: 'sun' }).icon] ?? 'sun'} size={18} />
                    <b>{d.hi}°</b>
                    <small>{d.lo}°</small>
                  </li>
                ))}
              </ol>
            )}
            <div className="tw-foot">
              <span className="tw-line">{sky.label}</span>
              <span className="tw-meta">{today.precip >= 20 ? `${today.precip}% chance of rain` : `Low ${today.lo}°`}</span>
            </div>
          </div>
        )}
      </Widget>
    ),
    clients: (
      <Widget key="clients" {...common('clients')} onOpen={() => navigate('/dashboard/clients')}>
        <div className="tw-fill">
          <div className="tw-fig">
            <b>{activeGroups.length}</b>
            <span>active</span>
          </div>
          {sizeOf('clients') === 'lg' && (
            <ul className="tw-agenda">
              {activeGroups.slice(0, 6).map((g) => (
                <li key={g.id}>
                  <i />
                  <span>{g.holdingName}</span>
                  <em>{g.deals.filter((d) => d.status !== 'Closed').length} open</em>
                </li>
              ))}
            </ul>
          )}
          <div className="tw-foot">
            <span className="tw-line">
              {openDeals.length} open {openDeals.length === 1 ? 'deal' : 'deals'}
            </span>
            <span className="tw-meta">{stageCounts[4].n} closed</span>
          </div>
        </div>
      </Widget>
    ),
    properties: (
      <Widget key="properties" {...common('properties')} aside={`${workable.length}`} onOpen={() => navigate('/dashboard/properties')}>
        {!recentAll.length ? (
          <div className="tw-empty">
            <b>No properties yet</b>
            <span>Add one from Properties</span>
          </div>
        ) : (
          <ul className="tw-picks">
            {recentAll.slice(0, sizeOf('properties') === 'lg' ? 5 : sizeOf('properties') === 'md' ? 2 : 1).map((p) => (
              <li key={p.property.id}>
                <button
                  type="button"
                  onClick={() => {
                    setSelectedProperty(p);
                    navigate('/dashboard/summary');
                  }}>
                  <span className="tw-thumb">
                    <Icon name="home" size={18} />
                  </span>
                  <span>
                    <b>{p.property.address}</b>
                    <small>
                      {[p.property.city, p.property.state].filter(Boolean).join(', ')}
                      {p.property.price ? ` · $${p.property.price.toLocaleString()}` : ''}
                    </small>
                  </span>
                  <Icon name="chev" size={14} className="chev" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </Widget>
    ),
    fees: (
      <Widget key="fees" {...common('fees')} aside={String(now.getFullYear())} onOpen={() => navigate('/dashboard/clients')}>
        {groupsLoading ? (
          <div className="tw-skel" />
        ) : (
          <div className="tw-fill">
            <div className="tw-fig">
              <b>{money(closedFees)}</b>
              <span>closed</span>
            </div>
            <div className="tw-foot">
              <span className="tw-line">{money(contractFees)} in contract</span>
              <span className="tw-meta">Expected fees, this year</span>
            </div>
          </div>
        )}
      </Widget>
    ),
    newin: (
      <Widget key="newin" {...common('newin')} aside={`${freshThisWeek.length} this week`} onOpen={() => navigate('/dashboard/properties')}>
        {!freshest.length ? (
          <div className="tw-empty">
            <b>Nothing new</b>
            <span>Updated properties show here</span>
          </div>
        ) : (
          <ul className="tw-picks">
            {freshest.slice(0, sizeOf('newin') === 'lg' ? 5 : sizeOf('newin') === 'md' ? 2 : 1).map((p) => (
              <li key={p.property.id}>
                <button
                  type="button"
                  onClick={() => {
                    setSelectedProperty(p);
                    navigate('/dashboard/summary');
                  }}>
                  <span className="tw-thumb">
                    <Icon name="bell" size={16} />
                  </span>
                  <span>
                    <b>{p.property.address}</b>
                    <small>Updated {ago(p.property.updatedAt)}</small>
                  </span>
                  <Icon name="chev" size={14} className="chev" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </Widget>
    ),
    notes: (
      <Widget key="notes" {...common('notes')}>
        <div className="tw-note">
          <textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="A note for today…" aria-label="Note" />
        </div>
      </Widget>
    ),
    wallpaper: (
      <Widget key="wallpaper" {...common('wallpaper')} aside={wall.name} onOpen={() => setPicking(true)}>
        <div className="tw-wallpaper" style={wall.css !== 'none' ? { backgroundImage: wall.thumb ?? wall.css } : undefined}>
          <b>{wall.css === 'none' ? 'Pick a wallpaper' : 'Change wallpaper'}</b>
        </div>
      </Widget>
    ),
  };

  const missing = (Object.keys(ABOUT) as WidgetId[]).filter((k) => !order.includes(k));

  return (
    <div
      className={`hx-today${hasWall ? ' wall' : ''}`}
      data-testid="today-view"
      onPointerDown={
        editing || picking
          ? (e) => {
              if (!(e.target as HTMLElement).closest('.tw, .tday-done, .tday-more')) ending();
            }
          : undefined
      }>
      <style>{CSS}</style>
      {hasWall && <div className="hx-wall" aria-hidden="true" style={{ backgroundImage: wall.css }} />}
      <div className="tday-in">
        <header className="tday-h">
          <div className="tday-kicker">{now.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</div>
          <h1>
            {greeting(now)}
            {firstName ? `, ${firstName}` : ''}
          </h1>
          {editing || picking ? (
            <button type="button" className="tday-done" onClick={ending}>
              Done
            </button>
          ) : (
            <Clock at={now} />
          )}
        </header>

        <div
          ref={grid}
          className={`tday-grid${editing ? ' editing' : ''}${settled ? ' settled' : ''}`}
          onClickCapture={
            editing
              ? (e) => {
                  if ((e.target as HTMLElement).closest('.tw-size, .tw-rm')) return;
                  e.stopPropagation();
                  e.preventDefault();
                }
              : undefined
          }>
          {order.map((k) => cards[k])}
          <i className="tw-slot" ref={slot} aria-hidden="true" />
        </div>

        {(editing || picking) && (
          <div className="tday-more">
            {editing && (
            <section aria-label="Add widgets">
              <div className="tday-kicker">Widgets</div>
              {missing.length ? (
                <div className="tday-shelf">
                  {missing.map((k) => (
                    <div key={k} className="tday-shelf-item">
                      <span className="ico">
                        <Icon name={ABOUT[k].icon} size={18} />
                      </span>
                      <span className="txt">
                        <b>{ABOUT[k].name}</b>
                        <small>{ABOUT[k].about}</small>
                      </span>
                      <button type="button" className="tw-add" aria-label={`Add ${ABOUT[k].name}`} onClick={() => add(k)}>
                        <Icon name="plus" size={14} stroke={2.4} />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="tday-shelf-none">Every widget is on Today. Remove one with its − to put it back here.</p>
              )}
            </section>
            )}
            <section aria-label="Wallpaper">
              <div className="tday-kicker">Wallpaper</div>
              <div className="tday-walls">
                {walls.map((w) => (
                  <div key={w.id} className={`tday-wall-tile${w.id === wall.id ? ' on' : ''}`}>
                    <button
                      type="button"
                      className="pick"
                      aria-pressed={w.id === wall.id}
                      aria-label={w.name}
                      style={w.css !== 'none' ? { backgroundImage: w.thumb ?? w.css } : undefined}
                      onClick={() => pickWall(w.id)}>
                      <span>{w.name}</span>
                    </button>
                    {w.own && (
                      <button type="button" className="x" aria-label={`Delete ${w.name}`} onClick={() => deleteOwn(w.id)}>
                        <Icon name="trash" size={12} />
                      </button>
                    )}
                  </div>
                ))}
                {ownWalls.length < OWN_MAX && (
                  <button type="button" className="tday-wall-add" disabled={wallBusy} onClick={() => fileIn.current?.click()}>
                    <Icon name="plus" size={18} stroke={2.2} />
                    <span>{wallBusy ? 'Adding…' : 'Add a photo'}</span>
                  </button>
                )}
                <input
                  ref={fileIn}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  hidden
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    e.target.value = '';
                    upload(f);
                  }}
                />
              </div>
              {wallErr && <div className="tday-err" role="alert">{wallErr}</div>}
            </section>
          </div>
        )}
      </div>
    </div>
  );
};

export default TodayView;
